/* eslint-disable react/display-name */
import {
  Button,
  FormControlLabel,
  Icon,
  LinearProgress,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import InputBase from '@material-ui/core/InputBase';
import { fade, makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import Dropzone from 'react-dropzone';
import fileImage from 'src/assets/img/fileUpload.svg';
import { s3 } from 'src/services/awsS3';
import { removeSpecialCharacters } from 'src/utils';
import styled from 'styled-components';

const useStyles = makeStyles(theme => ({
  root: {
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  button: {
    margin: theme.spacing(1),
  },
  progress: {
    width: '100%',
    marginTop: 15,
  },
  attachButton: {
    width: '100%',
    position: 'sticky',
    bottom: '2%',
  },
  search: {
    top: '-65px',
    display: 'inline-flex',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: fade(theme.palette.background.default, 0.75),
    '&:hover': {
      backgroundColor: fade(theme.palette.background.default, 1),
    },
    marginLeft: 0,
    marginBottom: '10px',
  },
  searchIcon: {
    padding: theme.spacing(0, 2),
    height: '2rem',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputRoot: {
    color: 'inherit',
  },
  inputInput: {
    padding: theme.spacing(1, 1, 1, 0),
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));

const DropBoxOut = styled.div`
  padding: 3em 1em;
  display: flex;
  justify-content: center;
  align-items: initial;
  border: 2px dashed #80808033;
  background-color: #fff;
  border-radius: 0.25em;
`;

const Scrollable = styled.div`
  max-height: calc(100vh - 510px);
  overflow: auto;
  display: flex;
  flex-direction: column;

  /* width */
  ::-webkit-scrollbar {
    width: 3px;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    background: #f1f1f100;
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: #88888829;
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const SearchBar = ({ searchTerm, handleSearch }) => {
  const classes = useStyles();
  return (
    <div className={classes.search}>
      <div className={classes.searchIcon}>
        <SearchIcon />
      </div>
      <InputBase
        placeholder="Search…"
        classes={{
          root: classes.inputRoot,
          input: classes.inputInput,
        }}
        value={searchTerm}
        onChange={handleSearch}
        inputProps={{ 'aria-label': 'search' }}
      />
    </div>
  );
};

const SingleUpload = forwardRef((props, ref) => {
  const classes = useStyles();
  const { bucket, path, maxKeys, accept, onImageSelect, hideFilesList = false } = props;
  const radioGroupRef = React.useRef(null);
  const [value, setValue] = React.useState(null);
  const [keysData, setKeysData] = useState([]);
  const [isPaginated, setIsPaginated] = useState(false);
  const [continuousToken, setContinuosToken] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [attachedFile, setAttachedFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchObjects(false);
    // eslint-disable-next-line
  }, []);

  useImperativeHandle(ref, () => ({
    handleOk(newValue) {
      if (newValue) {
        setValue(newValue);
      }

      return attachedFile;
    },
  }));

  const trimText = txt => txt.replace(path, '');
  const onDrop = async files => {
    if (files.length > 0) {
      const temp = {
        fileData: files[0],
        fileName: removeSpecialCharacters(files[0].name).replace(' ', '-'),
      };
      upload(temp);
    }
  };

  const updatedListParam = () => {
    if (isPaginated) {
      return {
        Bucket: bucket,
        MaxKeys: maxKeys,
        Prefix: path,
        ContinuationToken: continuousToken,
      };
    }
    return {
      Bucket: bucket,
      MaxKeys: maxKeys,
      Prefix: path,
    };
  };

  const fetchObjects = flag => {
    //   flag false means we need to concat, while true means fresh pull of data
    s3.listObjectsV2(updatedListParam(), (err, data) => {
      if (err) console.log(err, err.stack);
      else if (flag) {
        if (data.IsTruncated) {
          setIsPaginated(true);
          setKeysData(data.Contents);
          setContinuosToken(data.NextContinuationToken);
        } else {
          setIsPaginated(false);
          setKeysData(data.Contents);
        }
      } else if (data.IsTruncated) {
        setIsPaginated(true);
        setKeysData(keysData.concat(data.Contents));
        setContinuosToken(data.NextContinuationToken);
      } else {
        setIsPaginated(false);
        setKeysData(keysData.concat(data.Contents));
      }
    });
  };

  const upload = temp => {
    s3.upload(
      {
        Bucket: bucket,
        Key: path + temp.fileName,
        Body: temp.fileData,
      },
      err => {
        if (err) {
          console.log(err);
        }
        fetchObjects(true);
      }
    ).on('httpUploadProgress', progress => {
      const uploaded = parseInt((progress.loaded * 100) / progress.total);
      setUploadProgress(uploaded);
    });
  };

  const handleItemClick = val => {
    setAttachedFile(val);
  };

  const handleChange = event => {
    setValue(event.target.value);
    onImageSelect(event.target.value);
  };

  const handleSearch = e => {
    setSearchTerm(e.target.value);
  };

  const filteredData = keysData.filter(
    item => item.Key && item.Key.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <div className={classes.root} style={{ margin: '0' }}>
        <Dropzone onDrop={onDrop} multiple={false} minSize={0} accept={accept}>
          {({ getRootProps, getInputProps, isDragReject }) => (
            <section className="Container">
              <div {...getRootProps({ className: 'dropzone' })}>
                <input {...getInputProps()} />
                <div style={{ backgroundColor: '#fff' }}>
                  <DropBoxOut>
                    {!isDragReject && (
                      <>
                        <div style={{ marginRight: '20px', width: '25%' }}>
                          <img src={fileImage} alt="upload file" width="100%" />
                        </div>
                        <div>
                          <Typography variant="h6" color="initial">
                            Upload Files
                          </Typography>
                          <Typography variant="body2" color="initial" style={{ margin: '12px 0' }}>
                            Drop files here or click browse thorough your machine
                          </Typography>{' '}
                          <Button
                            variant="contained"
                            color="primary"
                            style={{ margin: '0px' }}
                            className={classes.button}
                            startIcon={<Icon>publish</Icon>}
                          >
                            Upload
                          </Button>
                        </div>
                      </>
                    )}
                    {isDragReject && (
                      <>
                        <div style={{ marginRight: '20px', width: '25%' }}>
                          <img src={fileImage} alt="upload file" width="100%" />
                        </div>
                        <div>
                          <Typography variant="h6" color="initial">
                            Multiple Files not supported!
                          </Typography>
                          <Typography variant="body2" color="initial" style={{ margin: '12px 0' }}>
                            Drop files here or click browse thorough your machine
                          </Typography>{' '}
                          <Button
                            variant="contained"
                            color="primary"
                            style={{ margin: '0px' }}
                            className={classes.button}
                            startIcon={<Icon>publish</Icon>}
                          >
                            Upload
                          </Button>
                        </div>
                      </>
                    )}
                  </DropBoxOut>
                  {uploadProgress > 0 && (
                    <div className={classes.progress}>
                      <LinearProgress variant="determinate" value={uploadProgress} />
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}
        </Dropzone>
        {!hideFilesList && (
          <RadioGroup
            ref={radioGroupRef}
            aria-label="ringtone"
            name="ringtone"
            value={value || value}
            onChange={handleChange}
          >
            <SearchBar searchTerm={searchTerm} handleSearch={handleSearch} />
            <Typography
              variant="h6"
              color="primary"
              style={{ background: 'aliceblue', padding: '0.5em 0.5em' }}
            >{`${filteredData.length} FILES`}</Typography>
            <Scrollable>
              {filteredData
                .filter(item => !item.Key.includes('.mp4'))
                .map(
                  (item, index) =>
                    !!trimText(item.Key) && (
                      <FormControlLabel
                        value={trimText(item.Key)}
                        key={index}
                        control={<Radio />}
                        onClick={() => handleItemClick(item)}
                        label={trimText(item.Key)}
                      />
                    )
                )}
            </Scrollable>
          </RadioGroup>
        )}

        {isPaginated && (
          <Button variant="contained" color="primary" onClick={() => fetchObjects(false)}>
            Load More...
          </Button>
        )}
      </div>
    </>
  );
});

export default SingleUpload;
